import { cloneDeep } from 'lodash'
import { normalHeadingTypes, coveringHeadingTypes, changeHeadingProperties } from '../ArticleParse'
import type { LayoutableSetting } from '../types'
import type { buildInitialValues } from './initialValues'

type ContextValues = ReturnType<typeof buildInitialValues>

const nullIfEmpty = (obj: number | string | undefined | null): number | null =>
  (obj === '' ? null : (obj ?? null)) as number | null

export const buildArticleHeadingsSubmitProps = (
  contextValues: ContextValues,
  setting: LayoutableSetting | null | undefined
) => {
  const {
    numberOfOccupyingColumns,
    numberOfOccupyingRows,
    headingOrientation,
    headingObjectStyleType,
    headingCombinationSettingType
  } = contextValues.rootHeading.data

  const updatedHeadings = Object.entries(contextValues.headings).reduce<typeof contextValues.headings>(
    (acc, [key, h]) => {
      const k = key as keyof typeof contextValues.headings
      if (h.data === undefined) {
        acc[k] = cloneDeep(h)
        return acc
      }

      const data = cloneDeep(h.data)

      if (normalHeadingTypes.has(data.headingType)) {
        data.orientation = headingOrientation
        data.headingObjectStyleType = headingObjectStyleType
      }

      const numberOfColumns =
        normalHeadingTypes.has(data.headingType) || coveringHeadingTypes.has(data.headingType)
          ? numberOfOccupyingColumns
          : undefined

      if (numberOfColumns != null) {
        changeHeadingProperties(data, numberOfColumns, headingCombinationSettingType ?? '', setting)
      }

      acc[k] = { data, base: h.base }
      return acc
    },
    {} as typeof contextValues.headings
  )

  const headings = Object.values(updatedHeadings)
    .map((h) => h.data)
    .filter((v): v is NonNullable<typeof v> => typeof v === 'object' && v !== null)
    .map((heading) => ({
      ...heading,
      numberOfOccupyingColumns: nullIfEmpty(heading.numberOfOccupyingColumns),
      numberOfOccupyingRows: nullIfEmpty(heading.numberOfOccupyingRows),
      numberOfOccupyingCharacters: nullIfEmpty(heading.numberOfOccupyingCharacters)
    }))

  return {
    headings,
    headingCombinationSettingType,
    headingNumberOfOccupyingColumns: numberOfOccupyingColumns,
    headingNumberOfOccupyingRows: numberOfOccupyingRows
  }
}
